import React from 'react';
import { TrendingUp, Activity, Calendar, Sun, Cloud, CloudRain, Award } from 'lucide-react';
import { UserStats } from '../types';

interface StatusDashboardProps {
  userStats: UserStats;
}

const StatusDashboard: React.FC<StatusDashboardProps> = ({ userStats }) => {
  // Calculate streak percentage for visual representation
  const streakPercentage = Math.min(100, (userStats.streak / 7) * 100);
  
  // Get mood data for the past week
  const weekMoods = userStats.moodHistory.slice(-7);
  
  // Map mood to icon and color
  const moodToIcon = {
    'great': { icon: <Sun className="w-5 h-5" />, color: 'text-yellow-500' },
    'good': { icon: <Sun className="w-5 h-5" />, color: 'text-orange-400' },
    'neutral': { icon: <Cloud className="w-5 h-5" />, color: 'text-gray-400' },
    'low': { icon: <CloudRain className="w-5 h-5" />, color: 'text-blue-400' },
    'bad': { icon: <CloudRain className="w-5 h-5" />, color: 'text-blue-600' },
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-4 w-full max-h-full overflow-y-auto">
      <h2 className="text-xl font-bold text-gray-800 mb-4">Your Status</h2>
      
      {/* Level and XP Progress */}
      <div className="mb-6 bg-gradient-to-r from-blue-500 to-indigo-600 p-4 rounded-lg text-white">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-medium">Level {userStats.level}</h3>
          <div className="flex items-center">
            <Award className="w-5 h-5 mr-1" />
            <span className="text-sm">{userStats.xp}/{userStats.xpToNextLevel} XP</span>
          </div>
        </div>
        <div className="w-full h-2 bg-white bg-opacity-20 rounded-full overflow-hidden">
          <div 
            className="h-full bg-white" 
            style={{ width: `${(userStats.xp / userStats.xpToNextLevel) * 100}%` }}
          ></div>
        </div>
      </div>
      
      {/* Streak */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-medium text-gray-700">Current Streak</h3>
          <span className="font-bold text-blue-500">{userStats.streak} days</span>
        </div>
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-orange-400 to-red-500" 
            style={{ width: `${streakPercentage}%` }}
          ></div>
        </div>
      </div>
      
      {/* Weekly Mood */}
      <div className="mb-6">
        <h3 className="font-medium text-gray-700 mb-3">Weekly Mood</h3>
        <div className="grid grid-cols-7 gap-1">
          {weekMoods.map((moodData, idx) => {
            const mood = moodData.mood;
            const moodInfo = moodToIcon[mood as keyof typeof moodToIcon];
            
            return (
              <div key={idx} className="flex flex-col items-center">
                <div className={`p-2 rounded-full ${moodInfo.color} bg-opacity-10 mb-1`}>
                  {moodInfo.icon}
                </div>
                <span className="text-xs text-gray-500">
                  {new Date(moodData.date).toLocaleDateString('en-US', { weekday: 'short' }).slice(0, 1)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
          <div className="flex items-center text-blue-500 mb-1">
            <TrendingUp className="w-4 h-4 mr-1" />
            <span className="text-sm font-medium">Focus Time</span>
          </div>
          <p className="text-xl font-bold">4.5h</p>
          <p className="text-xs text-gray-500">+1.2h from last week</p>
        </div>
        
        <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
          <div className="flex items-center text-green-500 mb-1">
            <Activity className="w-4 h-4 mr-1" />
            <span className="text-sm font-medium">Mindfulness</span>
          </div>
          <p className="text-xl font-bold">3 sessions</p>
          <p className="text-xs text-gray-500">this week</p>
        </div>
      </div>
    </div>
  );
};

export default StatusDashboard;